import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Paper,
  CircularProgress, Chip, IconButton, Tooltip,
  Menu, MenuItem, Checkbox, ListItemText, Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Storefront as StorefrontIcon,
  ImageNotSupported as NoImageIcon,
} from '@mui/icons-material';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { useResizableCols } from '../hooks/useResizableCols';
import ProductoImagenZoomDialog from './ProductoImagenZoomDialog';

// ─── Drag handle para resize ─────────────────────────────────────────────────
const ResizeHandle = ({ onMouseDown }) => {
  const [hover, setHover] = useState(false);
  return (
    <span
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 8,
        cursor: 'col-resize', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <span style={{
        width: hover ? 3 : 2, height: hover ? '70%' : '55%', borderRadius: 1,
        background: hover ? 'rgba(25,118,210,0.7)' : 'rgba(0,0,0,0.22)',
        pointerEvents: 'none', transition: 'all 0.12s',
      }} />
    </span>
  );
};

const COL_DEFAULTS = {
  codigo:       90,
  nombre:       210,
  categoria:    120,
  subcategoria: 120,
  marca:        100,
  modelo:       100,
  talla:        70,
  color:        70,
  precio:       70,
  stock:        72,
};

const COLS = [
  { key: 'codigo',       label: 'Código',       align: 'center', render: p => p.codigo || '' },
  { key: 'nombre',       label: 'Producto',     align: 'left',   render: p => <Tooltip title={p.nombre}><span>{p.nombre}</span></Tooltip> },
  { key: 'categoria',    label: 'Categoría',    align: 'center', render: p => p.categoria || '' },
  { key: 'subcategoria', label: 'Subcategoría', align: 'center', render: p => p.subcategoria || '' },
  { key: 'marca',        label: 'Marca',        align: 'center', render: p => p.marca || '' },
  { key: 'modelo',       label: 'Modelo',       align: 'center', render: p => p.modelo || '' },
  { key: 'talla',        label: 'Talla',        align: 'center', render: p => p.talla || '' },
  { key: 'color',        label: 'Color',        align: 'center', render: p => p.color || '' },
  { key: 'precio',       label: 'Precio',       align: 'right',  render: p => p.precio != null ? `S/ ${Number(p.precio).toFixed(2)}` : '' },
  {
    key: 'stock', label: 'Stock', align: 'center',
    render: p => (
      <Chip
        label={Number(p.stock_total).toFixed(0)}
        size="small"
        color={Number(p.stock_total) > 0 ? 'success' : 'default'}
        sx={{ fontSize: '0.7rem', height: 20 }}
      />
    ),
  },
];

const COLS_HIDDEN_KEY = 'producto-buscador-cols-hidden';

// ─── Sub-popup: stock por almacén ────────────────────────────────────────────
export function StockAlmacenesDialog({ open, onClose, productoId, productoNombre }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !productoId) return;
    setLoading(true);
    axios.get(`/api/productos/${productoId}/stock-almacenes`)
      .then(r => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, productoId]);

  const total = rows.reduce((s, r) => s + Number(r.stock || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <StorefrontIcon color="primary" fontSize="small" />
        <Box flex={1}>
          <Typography variant="subtitle1" fontWeight="bold">Stock por Almacén</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{productoNombre}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Almacén</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Stock</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography variant="caption" color="text.secondary">Sin movimientos en kardex</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r, i) => (
                  <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                    <TableCell>{r.almacen}</TableCell>
                    <TableCell align="right">{Number(r.stock).toFixed(2)}</TableCell>
                  </TableRow>
                ))
              )}
              {rows.length > 0 && (
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{total.toFixed(2)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Popup principal ──────────────────────────────────────────────────────────
export default function ProductoBuscadorDialog({ open, onClose, onSelect }) {
  const [q, setQ] = useState('');
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [stockOpen, setStockOpen] = useState(false);
  const [zoomImg, setZoomImg] = useState({ open: false, src: '', nombre: '', codigo: '', precio: null, productoId: null });
  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
      const saved = localStorage.getItem(COLS_HIDDEN_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return [];
  });
  const [colMenu, setColMenu] = useState(null);
  const inputRef = useRef(null);
  const { widths: colW, startResize } = useResizableCols('producto-buscador-cols', COL_DEFAULTS);

  const visibleCols = COLS.filter(c => !hiddenCols.includes(c.key));

  const persistHiddenCols = (next) => {
    setHiddenCols(next);
    try { localStorage.setItem(COLS_HIDDEN_KEY, JSON.stringify(next)); } catch {}
  };

  const handleHeaderContextMenu = (e) => {
    e.preventDefault();
    setColMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4 });
  };

  const toggleCol = (key) => {
    const isHidden = hiddenCols.includes(key);
    if (!isHidden && COLS.length - hiddenCols.length <= 1) return; // no ocultar la última visible
    persistHiddenCols(isHidden ? hiddenCols.filter(k => k !== key) : [...hiddenCols, key]);
  };

  const mostrarTodasCols = () => {
    persistHiddenCols([]);
    setColMenu(null);
  };

  const abrirZoom = (prod) => {
    setZoomImg({ open: true, src: prod.imagen, nombre: prod.nombre, codigo: prod.codigo, precio: prod.precio, productoId: prod.id });
  };

  const fetchProductos = useCallback(
    debounce(async (texto) => {
      setLoading(true);
      try {
        const r = await axios.get('/api/productos/buscar-popup', { params: { q: texto, limit: 50 } });
        setProductos(Array.isArray(r.data) ? r.data : []);
      } catch {
        setProductos([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [] // eslint-disable-line
  );

  useEffect(() => {
    if (open) {
      setQ('');
      setSelected(null);
      fetchProductos('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]); // eslint-disable-line

  const handleSearch = (e) => {
    setQ(e.target.value);
    fetchProductos(e.target.value);
  };

  const handleRowClick = (prod) => setSelected(prod);

  const handleRowDoubleClick = (prod) => {
    onSelect(prod);
    onClose();
  };

  const handleSeleccionar = () => {
    if (selected) { onSelect(selected); onClose(); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'F1' && selected) {
      e.preventDefault();
      setStockOpen(true);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
        maxWidth="xl" PaperProps={{ sx: { height: '82vh' } }}
        onKeyDown={handleKeyDown}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon color="primary" />
          <Typography variant="h6" flex={1}>Buscar Producto</Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </DialogTitle>

        {/* Buscador */}
        <Box sx={{ px: 3, pb: 1 }}>
          <TextField
            inputRef={inputRef}
            fullWidth size="small"
            placeholder="Buscar por código, nombre o descripción…"
            value={q}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            F1 = ver stock por almacén · Doble clic o Seleccionar para confirmar · Clic derecho en encabezado = ocultar columnas
          </Typography>
        </Box>

        <DialogContent sx={{ pt: 0, display: 'flex', gap: 2, overflow: 'hidden', pb: 1 }}>

          {/* Tabla izquierda con scroll solo cuando hace falta */}
          <Box flex={7} sx={{
            overflowX: 'auto',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 6, height: 6 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 3 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
          }}>
            <Table size="small" sx={{ tableLayout: 'fixed', minWidth: visibleCols.reduce((s, c) => s + (colW[c.key] || 80), 0) }}>
              <colgroup>
                {visibleCols.map(c => <col key={c.key} style={{ width: colW[c.key] }} />)}
              </colgroup>
              <TableHead>
                <TableRow onContextMenu={handleHeaderContextMenu}
                  sx={{ backgroundColor: '#2e8ae6', position: 'sticky', top: 0, zIndex: 1 }}>
                  {visibleCols.map(col => (
                    <TableCell key={col.key} align="center" sx={{
                      position: 'relative', backgroundColor: '#2e8ae6',
                      color: '#fff', fontWeight: 'bold',
                      whiteSpace: 'nowrap', overflow: 'hidden',
                      userSelect: 'none', px: 1, cursor: 'context-menu',
                    }}>
                      {col.label}
                      <ResizeHandle onMouseDown={e => startResize(col.key, e)} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleCols.length} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : productos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleCols.length} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">Sin resultados</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  productos.map(p => {
                    const isSel = selected?.id === p.id;
                    return (
                      <TableRow key={p.id}
                        onClick={() => handleRowClick(p)}
                        onDoubleClick={() => handleRowDoubleClick(p)}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: isSel ? '#bbdefb' : undefined,
                          '&:hover': { backgroundColor: isSel ? '#bbdefb' : '#f5f5f5' },
                        }}
                      >
                        {visibleCols.map(col => (
                          <TableCell key={col.key} align={col.align || 'center'}
                            sx={{ px: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {col.render(p)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            <Menu
              open={!!colMenu}
              onClose={() => setColMenu(null)}
              anchorReference="anchorPosition"
              anchorPosition={colMenu ? { top: colMenu.mouseY, left: colMenu.mouseX } : undefined}
            >
              <MenuItem dense onClick={mostrarTodasCols} disabled={hiddenCols.length === 0}>
                <ListItemText>Mostrar todas</ListItemText>
              </MenuItem>
              <Divider />
              {COLS.map(col => (
                <MenuItem key={col.key} dense onClick={() => toggleCol(col.key)} sx={{ py: 0.25 }}>
                  <Checkbox size="small" checked={!hiddenCols.includes(col.key)} sx={{ p: 0.5 }} />
                  <ListItemText primary={col.label} />
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Panel imagen derecha */}
          <Box flex={3} display="flex" flexDirection="column" alignItems="center" gap={1}
            sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5, minWidth: 170, maxWidth: 220 }}>
            {selected ? (
              <>
                <Box
                  onDoubleClick={() => abrirZoom(selected)}
                  sx={{
                    width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', backgroundColor: '#f9f9f9', borderRadius: 1,
                    overflow: 'hidden', border: '1px solid #eeeeee', cursor: 'zoom-in',
                  }}>
                  {selected.imagen ? (
                    <img src={selected.imagen} alt={selected.nombre}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Box display="flex" flexDirection="column" alignItems="center" color="text.disabled">
                      <NoImageIcon sx={{ fontSize: 48 }} />
                      <Typography variant="caption">Sin imagen</Typography>
                    </Box>
                  )}
                </Box>
                <Typography variant="subtitle2" fontWeight="bold" textAlign="center"
                  sx={{ wordBreak: 'break-word', fontSize: '0.8rem' }}>
                  {selected.nombre}
                </Typography>
                {selected.codigo && (
                  <Chip label={selected.codigo} size="small" variant="outlined" />
                )}
                <Box display="flex" flexDirection="column" alignItems="center" mt={0.5}>
                  <Typography variant="caption" color="text.secondary">Stock total</Typography>
                  <Typography variant="h6" fontWeight="bold"
                    color={Number(selected.stock_total) > 0 ? 'success.main' : 'text.disabled'}>
                    {Number(selected.stock_total).toFixed(0)}
                  </Typography>
                </Box>
                <Tooltip title="Ver stock por almacén (F1)">
                  <Button size="small" startIcon={<StorefrontIcon />}
                    onClick={() => setStockOpen(true)} variant="outlined" fullWidth>
                    Stock x Almacén
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center"
                height="100%" color="text.disabled" gap={1}>
                <NoImageIcon sx={{ fontSize: 48 }} />
                <Typography variant="caption" textAlign="center">
                  Selecciona un producto para ver su imagen
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={onClose} color="inherit" startIcon={<CloseIcon />}>Cerrar</Button>
          <Button onClick={handleSeleccionar} variant="contained" disabled={!selected}
            startIcon={<SearchIcon />}>
            Seleccionar
          </Button>
        </DialogActions>
      </Dialog>

      <StockAlmacenesDialog
        open={stockOpen}
        onClose={() => setStockOpen(false)}
        productoId={selected?.id}
        productoNombre={selected?.nombre}
      />

      <ProductoImagenZoomDialog
        open={zoomImg.open}
        onClose={() => setZoomImg({ open: false, src: '', nombre: '', codigo: '', precio: null, productoId: null })}
        src={zoomImg.src}
        nombre={zoomImg.nombre}
        codigo={zoomImg.codigo}
        precio={zoomImg.precio}
        productoId={zoomImg.productoId}
      />
    </>
  );
}
